import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, Plus, Wifi, Users, DollarSign, AlertCircle, Download, CheckCircle, XCircle, Search, ArrowUpDown } from 'lucide-react';

const PPPoEClientsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, suspended
  const [showImportModal, setShowImportModal] = useState(false);
  const [routers, setRouters] = useState([]);
  const [selectedRouter, setSelectedRouter] = useState('');
  const [secrets, setSecrets] = useState([]);
  const [loadingSecrets, setLoadingSecrets] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    loadClients();
    loadRouters();
  }, []);

  const loadClients = async () => {
    try {
      const [clientsResponse, routersResponse] = await Promise.all([
        api.get('/api/pppoe/clients'),
        api.get('/api/routers')
      ]);
      
      // Enriquecer clientes con información del router
      const clientsWithRouter = clientsResponse.data.map(client => {
        const router = routersResponse.data.find(r => r.id === client.router_id);
        return {
          ...client,
          router_info: router ? {
            name: router.name,
            ip: router.ip_address,
            port: router.port
          } : null
        };
      });
      
      setClients(clientsWithRouter);
      setRouters(routersResponse.data);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRouters = async () => {
    try {
      const response = await api.get('/api/routers');
      setRouters(response.data);
    } catch (error) {
      console.error('Error cargando routers:', error);
    }
  };

  const handleImportSecrets = async () => {
    if (!selectedRouter) {
      alert('⚠️ Selecciona un router primero');
      return;
    }

    // Prevenir múltiples clics
    if (loadingSecrets) {
      return;
    }

    setLoadingSecrets(true);
    try {
      const response = await api.post(`/api/pppoe/import/${selectedRouter}`);
      
      const { profiles_imported, secrets_imported, has_profiles, has_secrets } = response.data;
      
      // Escenarios según lo importado
      if (profiles_imported === 0 && secrets_imported === 0 && has_profiles && has_secrets) {
        // Ya estaban importados previamente
        alert(`ℹ️ Datos ya importados anteriormente\n\n` +
              `Este router ya tiene sus profiles y secrets importados.\n` +
              `Puedes verlos al crear planes o clientes.`);
      } else if (has_profiles && has_secrets) {
        alert(`✅ Importación completa!\n\n` +
              `📋 Profiles: ${profiles_imported} ${profiles_imported === 1 ? 'nuevo' : 'nuevos'}\n` +
              `👥 Secrets: ${secrets_imported} ${secrets_imported === 1 ? 'nuevo' : 'nuevos'}\n\n` +
              `✓ Ya puedes crear planes y ligar tus profiles\n` +
              `✓ Luego crea clientes y liga tus secrets`);
      } else if (has_secrets && !has_profiles) {
        alert(`⚠️ Solo se encontraron secrets\n\n` +
              `👥 ${secrets_imported} secrets importados\n\n` +
              `📋 Necesitas crear profiles en MikroTik primero.`);
      } else if (has_profiles && !has_secrets) {
        alert(`✅ Profiles importados\n\n` +
              `📋 ${profiles_imported} profiles importados\n\n` +
              `Ahora crea tus planes y luego tus clientes.`);
      } else {
        alert(`ℹ️ No se encontraron profiles ni secrets\n\n` +
              `Verifica que tu MikroTik tenga configurados:\n` +
              `• PPP Profiles\n` +
              `• PPP Secrets`);
      }
      
      setShowImportModal(false);
      setSelectedRouter('');
      
      // Recargar la página para mostrar los datos actualizados
      if (profiles_imported > 0 || secrets_imported > 0) {
        loadClients();
      }
    } catch (error) {
      console.error('Error importando:', error);
      const errorMsg = error.response?.data?.detail || 'Error al conectar con MikroTik';
      alert(`❌ Error al importar\n\n${errorMsg}`);
    } finally {
      setLoadingSecrets(false);
    }
  };

  const handleSuspend = async (clientId) => {
    if (!window.confirm('¿Suspender este cliente?')) return;

    try {
      await api.post(`/api/pppoe/clients/${clientId}/suspend`);
      alert('✅ Cliente suspendido');
      loadClients();
    } catch (error) {
      console.error('Error suspendiendo:', error);
      alert('❌ Error al suspender cliente');
    }
  };

  const handleActivate = async (clientId) => {
    try {
      await api.post(`/api/pppoe/clients/${clientId}/activate`);
      alert('✅ Cliente reactivado');
      loadClients();
    } catch (error) {
      console.error('Error reactivando:', error);
      alert('❌ Error al reactivar cliente');
    }
  };

  // Función de ordenamiento
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Función de comparación para ordenamiento
  const sortedClients = [...clients].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Manejo especial para campos anidados
    if (sortConfig.key === 'router_name') {
      aValue = a.router_info?.name || '';
      bValue = b.router_info?.name || '';
    }
    if (sortConfig.key === 'next_due_date') {
      aValue = a.next_due_date ? new Date(a.next_due_date).getTime() : 0;
      bValue = b.next_due_date ? new Date(b.next_due_date).getTime() : 0;
    }

    // Comparación de strings (alfabético)
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Filtrado por estado y búsqueda
  const filteredClients = sortedClients.filter(client => {
    // Filtro por estado
    if (filter !== 'all' && client.status !== filter) return false;

    // Filtro por búsqueda
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        client.name?.toLowerCase().includes(search) ||
        client.phone?.toLowerCase().includes(search) ||
        client.pppoe_username?.toLowerCase().includes(search) ||
        client.plan_name?.toLowerCase().includes(search) ||
        client.router_info?.name?.toLowerCase().includes(search)
      );
    }

    return true;
  });

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'bg-green-500', text: 'Activo', icon: CheckCircle },
      suspended: { color: 'bg-red-500', text: 'Suspendido', icon: XCircle },
      pending: { color: 'bg-yellow-500', text: 'Pendiente', icon: AlertCircle }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`${badge.color} text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-fit`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
            >
              <ArrowLeft className={isDark ? 'text-white' : 'text-gray-900'} />
            </button>
            <div>
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Clientes PPPoE
              </h1>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Gestiona tus clientes de internet fijo
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => setShowImportModal(true)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                isDark
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
              }`}
            >
              <Download className="w-5 h-5" />
              Importar
            </button>
            <button
              onClick={() => navigate('/pppoe/plans')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                isDark
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
              }`}
            >
              <Wifi className="w-5 h-5" />
              Planes
            </button>
            <button
              onClick={() => navigate('/pppoe/reports')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                isDark
                  ? 'bg-purple-700 hover:bg-purple-600 text-white'
                  : 'bg-purple-100 hover:bg-purple-200 text-purple-900'
              }`}
            >
              <DollarSign className="w-5 h-5" />
              Reportes
            </button>
            <button
              onClick={() => navigate('/pppoe/new')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Nuevo Cliente
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-3 items-center">
          <div className="flex gap-3">
            {['all', 'active', 'suspended'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                  filter === status
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : isDark
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {status === 'all' ? 'Todos' : status === 'active' ? 'Activos' : 'Suspendidos'}
                {status === 'all' && ` (${clients.length})`}
                {status === 'active' && ` (${clients.filter(c => c.status === 'active').length})`}
                {status === 'suspended' && ` (${clients.filter(c => c.status === 'suspended').length})`}
              </button>
            ))}
          </div>
          
          {/* Barra de búsqueda */}
          <div className="flex-1 md:max-w-md">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <input
                type="text"
                placeholder="Buscar cliente, usuario, plan, router..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border transition-colors ${
                  isDark
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
              />
            </div>
          </div>
        </div>

        {/* Clients Table */}
        {filteredClients.length === 0 ? (
          <div className={`text-center py-12 rounded-xl ${
            isDark ? 'bg-gray-800/50' : 'bg-white'
          } shadow-lg`}>
            <Wifi className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              No hay clientes {filter !== 'all' ? filter === 'active' ? 'activos' : 'suspendidos' : ''}
            </h3>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              {filter === 'all' ? 'Crea tu primer cliente PPPoE o importa secrets existentes' : 'No se encontraron clientes con este estado'}
            </p>
          </div>
        ) : (
          <div className={`rounded-xl shadow-lg overflow-hidden ${
            isDark ? 'bg-gray-800/50' : 'bg-white'
          }`}>
            <table className="w-full">
              <thead className={isDark ? 'bg-gray-700' : 'bg-gray-100'}>
                <tr>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${
                      isDark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Cliente
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${
                      isDark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => handleSort('plan_name')}
                  >
                    <div className="flex items-center gap-2">
                      Plan
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${
                      isDark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => handleSort('pppoe_username')}
                  >
                    <div className="flex items-center gap-2">
                      Usuario PPPoE
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${
                      isDark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => handleSort('router_name')}
                  >
                    <div className="flex items-center gap-2">
                      Router
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${
                      isDark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => handleSort('monthly_payment')}
                  >
                    <div className="flex items-center gap-2">
                      Mensualidad
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${
                      isDark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => handleSort('next_due_date')}
                  >
                    <div className="flex items-center gap-2">
                      Próximo Pago
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-80 transition-colors ${
                      isDark ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-2">
                      Estado
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>Acciones</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredClients.map((client) => (
                  <tr 
                    key={client.id}
                    className={`${isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} cursor-pointer`}
                    onClick={() => navigate(`/pppoe/clients/${client.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {client.name}
                        </div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {client.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                        {client.plan_name || 'N/A'}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {client.plan_speeds || ''}
                      </div>
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {client.pppoe_username}
                    </td>
                    <td className="px-6 py-4">
                      {client.router_info ? (
                        <div>
                          <div className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {client.router_info.name}
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                            {client.router_info.ip}:{client.router_info.port}
                          </div>
                        </div>
                      ) : (
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>N/A</span>
                      )}
                    </td>
                    <td className={`px-6 py-4 font-semibold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      ${client.monthly_payment}
                    </td>
                    <td className={`px-6 py-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {client.next_due_date ? new Date(client.next_due_date).toLocaleDateString('es-MX') : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(client.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        {client.status === 'active' ? (
                          <button
                            onClick={() => handleSuspend(client.id)}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                              isDark
                                ? 'bg-red-900/50 text-red-300 hover:bg-red-900'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            Suspender
                          </button>
                        ) : (
                          <button
                            onClick={() => handleActivate(client.id)}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                              isDark
                                ? 'bg-green-900/50 text-green-300 hover:bg-green-900'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            Reactivar
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/pppoe/clients/${client.id}`)}
                          className="bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
                        >
                          Ver
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Import Secrets Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`max-w-3xl w-full rounded-xl shadow-2xl ${
              isDark ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="p-6">
                <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  📥 Importar desde MikroTik
                </h2>
                <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Importa Profiles y Secrets PPPoE existentes de tu router MikroTik al sistema
                </p>

                {/* Mensaje de ayuda */}
                <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                  <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                    💡 <strong>Importante:</strong> Selecciona el router del cual quieres importar. 
                    {routers.length > 1 && ` Tienes ${routers.length} routers configurados.`}
                  </p>
                </div>

                <div className="mb-6">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    🖥️ Selecciona Router {routers.length > 1 && `(${routers.length} disponibles)`}
                  </label>
                  <select
                    value={selectedRouter}
                    onChange={(e) => setSelectedRouter(e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      selectedRouter 
                        ? isDark ? 'border-green-600 bg-gray-700 text-white' : 'border-green-500 bg-white text-gray-900'
                        : isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'
                    } focus:ring-2 focus:ring-blue-500 transition-all`}
                  >
                    <option value="">⚠️ Selecciona un router...</option>
                    {routers.map((router) => (
                      <option key={router.id} value={router.id}>
                        {router.name} - {router.ip_address}:{router.port}
                      </option>
                    ))}
                  </select>
                  {!selectedRouter && (
                    <p className={`mt-2 text-sm ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>
                      ⚠️ Debes seleccionar un router antes de importar
                    </p>
                  )}
                </div>

                <button
                  onClick={handleImportSecrets}
                  disabled={!selectedRouter || loadingSecrets}
                  className={`w-full px-6 py-3 rounded-lg font-semibold transition-all ${
                    !selectedRouter || loadingSecrets
                      ? 'bg-gray-400 cursor-not-allowed opacity-50'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-xl hover:scale-[1.02]'
                  } text-white`}
                >
                  {loadingSecrets ? '⏳ Importando... Por favor espera' : '📥 Importar Profiles y Secrets'}
                </button>
                
                {loadingSecrets && (
                  <p className={`mt-3 text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Conectando al MikroTik y obteniendo datos...
                  </p>
                )}

                {/* Secrets List */}
                {secrets.length > 0 && (
                  <div className="mt-6 max-h-96 overflow-y-auto">
                    <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Secrets encontrados: {secrets.length}
                    </h3>
                    <div className="space-y-2">
                      {secrets.map((secret, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border ${
                            isDark
                              ? 'bg-gray-700/50 border-gray-600'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {secret.name}
                          </div>
                          <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Service: {secret.service} | Profile: {secret.profile || 'default'}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      💡 Para registrar estos clientes en el sistema, usa el botón "Nuevo Cliente"
                    </p>
                  </div>
                )}

                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setSecrets([]);
                    setSelectedRouter('');
                  }}
                  className={`w-full mt-4 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    isDark
                      ? 'bg-gray-700 hover:bg-gray-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PPPoEClientsPage;
